import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ymdLocal } from "@/lib/utils";
import { Plus, Trash2, AlertCircle, Sparkles, Send, CheckCircle2, MapPin, User, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useSport, SPORT_META } from "@/context/SportContext";

// Small badge used across Availability + Book pages to signal who books the
// court. Kept inline (also inlined in StudentBookNext) so the meaning stays
// visually consistent everywhere.
export function CourtChip({ value }) {
  if (!value) return null;
  const meta = {
    coach_provides: { label: "Court incl.", Icon: MapPin, color: "#CCFF00" },
    student_provides: { label: "You organise court", Icon: User, color: "#FF4500" },
    tbc: { label: "Court TBC", Icon: HelpCircle, color: "#A3A3A3" },
  }[value];
  if (!meta) return null;
  const { Icon, color, label } = meta;
  return (
    <span
      className="text-[10px] uppercase tracking-wider label-mono px-2 py-0.5 border inline-flex items-center gap-1"
      style={{ color, borderColor: color }}
      data-testid={`court-chip-${value}`}
    >
      <Icon className="w-2.5 h-2.5" /> {label}
    </span>
  );
}

const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return ymdLocal(d); };
const nextDay = (ymd) => { const [y, m, d] = ymd.split("-").map(Number); return ymdLocal(new Date(y, m - 1, d + 1)); };
const newRow = (date = tomorrow(), start_time = "18:00", end_time = "19:30") => ({ key: Math.random().toString(36).slice(2), date, start_time, end_time });
const UK_TZ = { timeZone: "Europe/London" };
const fmtDay = (iso) => new Date(iso).toLocaleDateString("en-GB", { ...UK_TZ, weekday: "short", day: "numeric", month: "short" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-GB", { ...UK_TZ, hour: "2-digit", minute: "2-digit" });
const fmtEntry = (w) => `${fmtDay(w.start)} · ${fmtTime(w.start)}–${fmtTime(w.end)}`;

export default function Availability() {
  const { sport } = useSport();
  const meta = SPORT_META[sport] || SPORT_META.pickleball;

  // Ad-hoc availability windows
  const [windows, setWindows] = useState([]);
  const [rows, setRows] = useState([newRow()]);
  const [removedNotice, setRemovedNotice] = useState([]);
  const [winForm, setWinForm] = useState({
    sport,
    notes: "",
    court_responsibility: "coach_provides",
  });
  const [creatingWin, setCreatingWin] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [publishForm, setPublishForm] = useState({}); // {winId: {price, max_spaces, session_type, title}}

  // Unavailability blocks (legacy)
  const [blocks, setBlocks] = useState([]);
  const today = ymdLocal(new Date());
  const [blockForm, setBlockForm] = useState({ start: today, end: today, reason: "" });

  const loadWindows = async () => {
    const { data } = await api.get("/coach/windows/mine");
    setWindows(data);
  };
  const loadBlocks = async () => {
    const { data } = await api.get("/availability");
    setBlocks(data);
  };

  useEffect(() => {
    loadWindows(); loadBlocks();
    api.get("/coach/windows/migration-notice").then((r) => setRemovedNotice(r.data.removed || [])).catch(() => {});
  }, []);

  const dismissNotice = async () => {
    setRemovedNotice([]);
    try { await api.post("/coach/windows/migration-notice/dismiss"); } catch { /* non-fatal */ }
  };
  const updateRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => {
    const last = rs[rs.length - 1];
    return [...rs, last ? newRow(nextDay(last.date), last.start_time, last.end_time) : newRow()];
  });
  const removeRow = (key) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs));

  // Update the sport on the window form whenever the app-level sport switches.
  useEffect(() => { setWinForm((f) => ({ ...f, sport })); }, [sport]);

  const addWindow = async (e) => {
    e.preventDefault();
    const bad = rows.findIndex((r) => !r.date || !r.start_time || !r.end_time || r.end_time <= r.start_time);
    if (bad >= 0) { toast.error(`Day ${bad + 1}: end time must be after start time`); return; }
    setCreatingWin(true);
    try {
      const { data } = await api.post("/coach/windows/batch", {
        sport: winForm.sport,
        entries: rows.map(({ date, start_time, end_time }) => ({ date, start_time, end_time })),
        notes: winForm.notes,
        court_responsibility: winForm.court_responsibility,
      });
      toast.success(`${data.created} availability ${data.created === 1 ? "entry" : "entries"} added`);
      setRows([newRow()]);
      setWinForm({ sport, notes: "", court_responsibility: winForm.court_responsibility });
      loadWindows();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add window");
    } finally { setCreatingWin(false); }
  };

  const removeWindow = async (id) => {
    if (!confirm("Delete this window?")) return;
    try {
      await api.delete(`/coach/windows/${id}`);
      toast.success("Window removed");
      loadWindows();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const publishWindow = async (w) => {
    const form = publishForm[w.id] || {};
    setPublishingId(w.id);
    try {
      await api.post(`/coach/windows/${w.id}/publish`, {
        session_type: form.session_type || "one_on_one",
        max_spaces: parseInt(form.max_spaces || 1, 10),
        price_per_player: parseFloat(form.price_per_player || 30),
        title: form.title || undefined,
      });
      toast.success("Window published — students can now book it");
      loadWindows();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to publish");
    } finally { setPublishingId(null); }
  };

  const addBlock = async (e) => {
    e.preventDefault();
    if (blockForm.end < blockForm.start) { toast.error("End date must be after start"); return; }
    try {
      await api.post("/availability", blockForm);
      toast.success("Unavailable block added");
      setBlockForm({ start: today, end: today, reason: "" });
      loadBlocks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const removeBlock = async (id) => {
    try { await api.delete(`/availability/${id}`); toast.success("Removed"); loadBlocks(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const parseYmd = (s) => {
    const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };
  const daysBetween = (a, b) => Math.max(1, Math.round((parseYmd(b) - parseYmd(a)) / 86400000) + 1);

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <div className="label-mono mb-2" style={{ color: meta.color }}>AVAILABILITY</div>
        <h1 className="h-display text-5xl tracking-tighter">When are you free?</h1>
        <div className="text-sm text-[#A3A3A3] mt-1">
          Add each day you're free with its own start and end time, so students see exactly when to book you. Publish any window to make it directly bookable.
        </div>
      </div>

      {removedNotice.length > 0 && (
        <div className="card-surface p-5 border-l-4 border-[#FF9F1C] fade-up" data-testid="window-migration-notice">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="label-mono text-[#FF9F1C] mb-1">AVAILABILITY UPDATED</div>
              <div className="text-sm text-white">
                Availability is now set day by day. These multi-day entries couldn't be converted cleanly, so they were removed. Please re-add them one day at a time:
              </div>
              <ul className="mt-2 space-y-1 text-xs text-[#E5E5E5]" data-testid="window-migration-list">
                {removedNotice.map((w) => (
                  <li key={w.id} className="font-mono">
                    {fmtDay(w.start)} {fmtTime(w.start)} → {fmtDay(w.end)} {fmtTime(w.end)} · {w.sport}{w.notes ? ` · ${w.notes}` : ""}
                  </li>
                ))}
              </ul>
            </div>
            <button type="button" onClick={dismissNotice} data-testid="window-migration-dismiss" className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#FF9F1C] text-black hover:opacity-90 shrink-0">
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Add day-by-day availability */}
      <form onSubmit={addWindow} className="card-surface p-6 fade-up d1 space-y-4" data-testid="window-form">
        <div className="space-y-2" data-testid="win-rows">
          {rows.map((r, i) => (
            <div key={r.key} className="grid grid-cols-12 gap-3 items-end" data-testid={`win-row-${i}`}>
              <div className="col-span-12 sm:col-span-5">
                {i === 0 && <div className="label-mono mb-2">Date</div>}
                <input type="date" required min={ymdLocal(new Date())} value={r.date} onChange={(e) => updateRow(r.key, { date: e.target.value })} data-testid={`win-date-${i}`}
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none" />
              </div>
              <div className="col-span-5 sm:col-span-3">
                {i === 0 && <div className="label-mono mb-2">Start time</div>}
                <input type="time" required step={900} value={r.start_time} onChange={(e) => updateRow(r.key, { start_time: e.target.value })} data-testid={`win-start-${i}`}
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none" />
              </div>
              <div className="col-span-5 sm:col-span-3">
                {i === 0 && <div className="label-mono mb-2">End time</div>}
                <input type="time" required step={900} value={r.end_time} onChange={(e) => updateRow(r.key, { end_time: e.target.value })} data-testid={`win-end-${i}`}
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none" />
              </div>
              <div className="col-span-2 sm:col-span-1 flex justify-center pb-2.5">
                <button type="button" onClick={() => removeRow(r.key)} disabled={rows.length === 1} data-testid={`win-row-remove-${i}`}
                  className="text-[#A3A3A3] hover:text-[#FF4500] disabled:opacity-20" aria-label="Remove day">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          <button type="button" onClick={addRow} data-testid="win-add-row"
            className="text-xs uppercase tracking-wider text-[#CCFF00] hover:opacity-80 inline-flex items-center gap-1 pt-1">
            <Plus className="w-3.5 h-3.5" /> Add another day
          </button>
        </div>
        <div className="grid md:grid-cols-12 gap-3 items-end pt-4 border-t border-[#262626]">
          <div className="md:col-span-4">
            <div className="label-mono mb-2">Court</div>
            <select value={winForm.court_responsibility} onChange={(e) => setWinForm({ ...winForm, court_responsibility: e.target.value })} data-testid="win-court"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none">
              <option value="coach_provides">Coach provides</option>
              <option value="student_provides">Student organises</option>
              <option value="tbc">To be confirmed</option>
            </select>
          </div>
          <div className="md:col-span-5">
            <div className="label-mono mb-2">Notes</div>
            <input value={winForm.notes} onChange={(e) => setWinForm({ ...winForm, notes: e.target.value })} placeholder="Kitchen play @ Riverside" data-testid="win-notes"
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none" />
          </div>
          <div className="md:col-span-3">
            <button type="submit" disabled={creatingWin} className="btn-volt w-full py-2.5 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="win-add">
              <Plus className="w-4 h-4" /> {creatingWin ? "Saving…" : `Save ${rows.length} day${rows.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </form>

      {/* Windows list */}
      <div className="space-y-3 fade-up d2">
        <div className="label-mono">Upcoming windows ({windows.length})</div>
        {!windows.length && (
          <div className="text-sm text-[#A3A3A3]" data-testid="windows-empty">
            No availability windows yet. Add one above — students on queued session plans won't be able to book you until you do.
          </div>
        )}
        {windows.map((w) => {
          const pForm = publishForm[w.id] || {};
          const isPublished = !!w.converted_offering_id;
          return (
            <div key={w.id} className="card-surface p-5" data-testid={`window-${w.id}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold" data-testid={`window-when-${w.id}`}>{fmtEntry(w)}</div>
                    <span className="text-[10px] uppercase tracking-wider label-mono px-2 py-0.5 border" style={{ color: SPORT_META[w.sport]?.color, borderColor: SPORT_META[w.sport]?.color }}>
                      {w.sport}
                    </span>
                    <CourtChip value={w.court_responsibility} />
                    {isPublished && (
                      <span className="text-[10px] uppercase tracking-wider label-mono px-2 py-0.5 border text-[#CCFF00] border-[#CCFF00] inline-flex items-center gap-1" data-testid={`window-published-${w.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Published
                      </span>
                    )}
                  </div>
                  {w.notes && <div className="text-xs text-[#A3A3A3] mt-1">{w.notes}</div>}
                </div>
                {!isPublished && (
                  <button onClick={() => removeWindow(w.id)} data-testid={`del-window-${w.id}`} className="text-[#A3A3A3] hover:text-[#FF4500]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {!isPublished && (
                <div className="mt-4 pt-4 border-t border-[#262626] grid md:grid-cols-12 gap-3 items-end">
                  <div className="md:col-span-3">
                    <div className="label-mono mb-1">Session type</div>
                    <select value={pForm.session_type || "one_on_one"} onChange={(e) => setPublishForm((p) => ({ ...p, [w.id]: { ...pForm, session_type: e.target.value } }))} data-testid={`pub-type-${w.id}`}
                      className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none">
                      <option value="one_on_one">1:1</option>
                      <option value="group">Group</option>
                      <option value="performance">Performance</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <div className="label-mono mb-1">Spaces</div>
                    <input type="number" min={1} max={20} value={pForm.max_spaces || 1} onChange={(e) => setPublishForm((p) => ({ ...p, [w.id]: { ...pForm, max_spaces: e.target.value } }))} data-testid={`pub-spaces-${w.id}`}
                      className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <div className="label-mono mb-1">Price / player</div>
                    <input type="number" min={0} step="0.5" value={pForm.price_per_player ?? 30} onChange={(e) => setPublishForm((p) => ({ ...p, [w.id]: { ...pForm, price_per_player: e.target.value } }))} data-testid={`pub-price-${w.id}`}
                      className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none" />
                  </div>
                  <div className="md:col-span-3">
                    <div className="label-mono mb-1">Title (optional)</div>
                    <input value={pForm.title || ""} onChange={(e) => setPublishForm((p) => ({ ...p, [w.id]: { ...pForm, title: e.target.value } }))} placeholder="e.g. Kitchen dinks lab" data-testid={`pub-title-${w.id}`}
                      className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <button
                      onClick={() => publishWindow(w)}
                      disabled={publishingId === w.id}
                      data-testid={`pub-window-${w.id}`}
                      className="btn-volt w-full py-2.5 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" /> {publishingId === w.id ? "Publishing…" : "Publish"}
                    </button>
                  </div>
                </div>
              )}
              {isPublished && (
                <div className="mt-3 pt-3 border-t border-[#262626] text-xs text-[#A3A3A3]">
                  Bookable via <Link to="/offerings" className="text-[#CCFF00] hover:underline">/offerings</Link>. Cancel the offering there to release the slot.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Unavailability blocks (legacy) */}
      <details className="fade-up d3" data-testid="block-off-collapse">
        <summary className="cursor-pointer label-mono text-[#A3A3A3] hover:text-white">Block off days (holiday / travel)</summary>
        <div className="mt-4 space-y-4">
          <div className="text-sm text-[#A3A3A3]">
            Mark whole days as <em>unavailable</em> to hide yourself from public offerings and block enrolments.
          </div>
          <form onSubmit={addBlock} className="card-surface p-4 grid md:grid-cols-12 gap-3 items-end" data-testid="availability-form">
            <div className="md:col-span-3">
              <div className="label-mono mb-2">Start date</div>
              <input type="date" required value={blockForm.start} onChange={(e) => setBlockForm({ ...blockForm, start: e.target.value })} data-testid="avail-start"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none" />
            </div>
            <div className="md:col-span-3">
              <div className="label-mono mb-2">End date</div>
              <input type="date" required value={blockForm.end} onChange={(e) => setBlockForm({ ...blockForm, end: e.target.value })} data-testid="avail-end"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none" />
            </div>
            <div className="md:col-span-4">
              <div className="label-mono mb-2">Reason (optional)</div>
              <input value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} placeholder="e.g. Tournament travel" data-testid="avail-reason"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none" />
            </div>
            <div className="md:col-span-2">
              <button type="submit" className="btn-volt w-full py-2.5 text-xs inline-flex items-center justify-center gap-2" data-testid="avail-add">
                <Plus className="w-4 h-4" /> Add block
              </button>
            </div>
          </form>

          <div className="space-y-3">
            <div className="label-mono">Blocked ranges ({blocks.length})</div>
            {!blocks.length && <div className="text-sm text-[#A3A3A3]">No unavailability blocks yet.</div>}
            {blocks.map((b) => (
              <div key={b.id} className="card-surface p-4 flex items-center gap-4" data-testid={`avail-${b.id}`}>
                <AlertCircle className="w-5 h-5 text-[#FF4500]" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">
                    {parseYmd(b.start).toLocaleDateString(undefined, { day: "numeric", month: "short" })} → {parseYmd(b.end).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                  <div className="text-xs text-[#A3A3A3]">{b.reason || "Unavailable"}</div>
                </div>
                <div className="label-mono text-[#FF4500]">{daysBetween(b.start, b.end)}d</div>
                <button onClick={() => removeBlock(b.id)} data-testid={`del-avail-${b.id}`} className="text-[#FF4500] hover:opacity-70"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </details>

      <div className="text-xs text-[#666] fade-up d4 inline-flex items-center gap-2">
        <Sparkles className="w-3 h-3" />
        Tip: publish 1–2 windows a week to keep the coach nudge quiet and your booking rate up.
      </div>
    </div>
  );
}
